import {
  Box,
  debounce,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getAccessToken, useAuth } from "../../context/auth";
import {
  GetMapConfig,
  ResolveMapAddress,
  SearchLegacyDigiMapAddress,
  SearchMapAddress,
  type MapRuntimeConfig,
  type MapSuggestion,
} from "@/services/api/address-service";
import { MapContainer, Marker } from "react-leaflet";
import { AuthTileLayer } from "./AuthTileLayer";
import { environment } from "@/environment/environment";
import type { IAddress } from "@/services/model/address";

type Props = {
  addressFields: IAddress;
  setAddressFields: React.Dispatch<React.SetStateAction<IAddress>>;
  enableMap?: boolean;
  token?: string;
  GetAddressList?: (token: string) => Promise<any>;
  showDialog?: (title: string, message: string, success?: boolean) => void;
};

type InternalAddress = {
  position: [number, number];
  validAddress: boolean;
};

type AddressResponse = {
  id: string;
  googleAddress: string;
  latitude: number;
  longitude: number;
};

const DEFAULT_POSITION: [number, number] = [-26.2041, 28.0473];

const createSessionToken = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function DigiMapInner({
  addressFields,
  setAddressFields,
  enableMap = true,
  token: tokenProp,
  GetAddressList,
  showDialog,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [mapConfig, setMapConfig] = useState<MapRuntimeConfig | null>(null);
  const [useLegacyEndpoints, setUseLegacyEndpoints] = useState(false);
  const [internalAddress, setInteralAddressValue] = useState<InternalAddress>({
    position: [
      addressFields.latitude == null
        ? DEFAULT_POSITION[0]
        : Number(addressFields.latitude),
      addressFields.longitude == null
        ? DEFAULT_POSITION[1]
        : Number(addressFields.longitude),
    ],
    validAddress:
      addressFields.googleAddress != null && addressFields.googleAddress !== "",
  });
  const [suggestions, setSuggestions] = useState<MapSuggestion[]>([]);

  const { authTokens } = useAuth();
  const fallbackToken = getAccessToken(authTokens) ?? "";
  const token = tokenProp ?? fallbackToken;

  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sessionTokenRef = useRef<string>(createSessionToken());

  const url = useLegacyEndpoints
    ? environment.baseUrl + "/address/{z}/{x}/{y}.png"
    : environment.baseUrl + "/maps/tiles/{z}/{x}/{y}.png";

  const [savedAddresses, setSavedAddresses] = useState<AddressResponse[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoadingConfig(true);

    void GetMapConfig(token)
      .then((response) => {
        if (!cancelled) {
          if (response?.resultCode && response.data) {
            setMapConfig((response.data as MapRuntimeConfig | undefined) ?? null);
            setUseLegacyEndpoints(false);
          } else {
            setMapConfig(null);
            setUseLegacyEndpoints(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingConfig(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (
      addressFields.googleAddress &&
      addressFields.googleAddress !== inputValue
    ) {
      setInputValue(addressFields.googleAddress);
    }

    if (
      addressFields.latitude != null &&
      addressFields.longitude != null &&
      !Number.isNaN(Number(addressFields.latitude)) &&
      !Number.isNaN(Number(addressFields.longitude))
    ) {
      setInteralAddressValue({
        position: [
          Number(addressFields.latitude),
          Number(addressFields.longitude),
        ],
        validAddress: true,
      });
    }
  }, [
    addressFields.googleAddress,
    addressFields.latitude,
    addressFields.longitude,
    inputValue,
  ]);

  const searchAddress = useMemo(
    () =>
      debounce(async (address: string) => {
        if (!address || address.length < 3 || !token) {
          setSuggestions([]);
          return;
        }

        setLoading(true);

        try {
          const response = useLegacyEndpoints
            ? await SearchLegacyDigiMapAddress(token, address)
            : await SearchMapAddress(token, address, sessionTokenRef.current);

          if (!useLegacyEndpoints && response?.resultCode === false) {
            const legacyResponse = await SearchLegacyDigiMapAddress(
              token,
              address,
            );
            const legacySuggestions = Array.isArray(legacyResponse?.data)
              ? legacyResponse.data
              : [];
            setUseLegacyEndpoints(true);
            setSuggestions(legacySuggestions);
            setOpen(legacySuggestions.length > 0);
          } else if (Array.isArray(response?.data) && response.data.length > 0) {
            setSuggestions(response.data);
            setOpen(true);
          } else {
            setSuggestions([]);
            setOpen(false);
          }
        } finally {
          setLoading(false);
        }
      }, 400),
    [token, useLegacyEndpoints],
  );

  useEffect(() => {
    return () => {
      searchAddress.clear();
    };
  }, [searchAddress]);

  const handleChange = async (value: string) => {
    setInputValue(value);
    if (value.trim().length === 0) {
      sessionTokenRef.current = createSessionToken();
      setInteralAddressValue({
        position: DEFAULT_POSITION,
        validAddress: false,
      });
      setAddressFields((prev) => ({
        ...prev,
        googleAddress: "",
        latitude: undefined,
        longitude: undefined,
      }));
      setSuggestions([]);
      setOpen(false);
      return;
    }

    await searchAddress(value);
  };

  const applyResolvedAddress = (
    label: string,
    latitude: number,
    longitude: number,
  ) => {
    setInputValue(label);
    setAddressFields((prev) => ({
      ...prev,
      googleAddress: label,
      latitude,
      longitude,
    }));
    setInteralAddressValue({
      position: [latitude, longitude],
      validAddress: true,
    });
    setSuggestions([]);
    setOpen(false);
    sessionTokenRef.current = createSessionToken();
  };

  const handleAddressSelected = async (value: MapSuggestion) => {
    if (!value.requiresResolve && value.latitude != null && value.longitude != null) {
      applyResolvedAddress(value.label, value.latitude, value.longitude);
      return;
    }

    const response = await ResolveMapAddress(token, {
      id: value.id,
      provider: value.provider,
      label: value.label,
      sessionToken: sessionTokenRef.current,
    });

    if (response?.resultCode && response.data) {
      applyResolvedAddress(
        response.data.label,
        Number(response.data.latitude),
        Number(response.data.longitude),
      );
    }
  };

  const fetchSavedAddresses = useCallback(async () => {
    if (!GetAddressList || !token) {
      return;
    }

    setLoadingExisting(true);
    try {
      const responseList = await GetAddressList(token);
      if (responseList && responseList.data && Array.isArray(responseList.data)) {
        setSavedAddresses(
          responseList.data.map((r: AddressResponse) => ({
            id: r.id,
            googleAddress: r.googleAddress,
            latitude: r.latitude,
            longitude: r.longitude,
          })),
        );
      } else {
        const msg =
          "There seems to be a problem with your internet connection.\nPlease check your internet connection.";
        showDialog ? showDialog("Failure", msg) : console.error(msg);
      }
    } catch (err) {
      const msg =
        "An exception occurred while loading saved addresses, please contact your administrator.";
      showDialog ? showDialog("Failure", msg) : console.error(msg);
      if (err instanceof Error) console.error(err.message);
    } finally {
      setLoadingExisting(false);
    }
  }, [GetAddressList, token, showDialog]);

  const handleOpenExistingClick = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);

    if (savedAddresses.length === 0) {
      await fetchSavedAddresses();
    }
  };

  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleExistingAddressSelected = (addr: AddressResponse) => {
    applyResolvedAddress(addr.googleAddress, addr.latitude, addr.longitude);
    handleCloseMenu();
  };

  const reactLeaflet = useMemo(() => {
    return (
      <>
        {enableMap && (useLegacyEndpoints || mapConfig?.supportsTiles) && (
          <MapContainer
            key={`${internalAddress.position[0]}_${internalAddress.position[1]}`}
            center={internalAddress.position}
            zoom={internalAddress.validAddress ? 15 : 5}
            scrollWheelZoom={false}
            preferCanvas={true}
            attributionControl={false}
            style={{ height: "100%" }}
          >
            <AuthTileLayer
              url={url}
              token={token}
              tileSize={512}
              zoomOffset={-1}
            />
            {internalAddress.validAddress && (
              <Marker position={internalAddress.position} />
            )}
          </MapContainer>
        )}
      </>
    );
  }, [
    enableMap,
    internalAddress,
    mapConfig?.supportsTiles,
    token,
    url,
    useLegacyEndpoints,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: enableMap ? "30vh" : "auto",
        width: "100%",
      }}
    >
      <TextField
        type="text"
        label="Search address or business"
        fullWidth
        value={inputValue}
        inputRef={inputRef}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => {
          setTimeout(() => setOpen(false), 200);
        }}
        InputProps={{
          endAdornment: (
            <Tooltip title="Use an existing address">
              <IconButton
                size="small"
                onClick={handleOpenExistingClick}
                tabIndex={-1}
              >
                {loadingExisting ? (
                  <CircularProgress size={18} />
                ) : (
                  <ArrowDropDownIcon />
                )}
              </IconButton>
            </Tooltip>
          ),
        }}
      />
      {(loading || loadingConfig) && <LinearProgress color="primary" />}

      {suggestions.length > 0 && !loading && (
        <Popper
          open={open && suggestions.length > 0}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ zIndex: 13000 }}
        >
          <Paper
            elevation={4}
            sx={{
              mt: 1,
              maxHeight: 200,
              overflowY: "auto",
              width: inputRef.current?.offsetWidth ?? 300,
            }}
          >
            <List disablePadding>
              {suggestions.map((suggestion) => (
                <ListItemButton
                  sx={{ fontSize: 12 }}
                  key={`${suggestion.provider}-${suggestion.id}`}
                  onClick={() => handleAddressSelected(suggestion)}
                >
                  {suggestion.label}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {loadingExisting && (
          <MenuItem disabled>
            <CircularProgress size={18} style={{ marginRight: 8 }} />
            Loading addresses...
          </MenuItem>
        )}
        {!loadingExisting && savedAddresses.length === 0 && (
          <MenuItem disabled>No saved addresses found</MenuItem>
        )}
        {!loadingExisting &&
          savedAddresses.map((addr) => (
            <MenuItem
              key={addr.id}
              onClick={() => handleExistingAddressSelected(addr)}
            >
              {addr.googleAddress}
            </MenuItem>
          ))}
      </Menu>

      {enableMap && !useLegacyEndpoints && mapConfig && !mapConfig.supportsTiles && (
        <Typography variant="caption" color="text.secondary">
          Map preview is not configured for this client.
        </Typography>
      )}

      {reactLeaflet}
    </Box>
  );
}
